// Transport Codes by Mode API will be defined after authBaseQuery
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { JobServiceEntity, GetAllWOServicesParams } from '../types/jobservice.type';
import { SignatureDto } from '../types/jobservice.type';
import { WoServiceAudit } from '../types/jobservice.type';
import { getToken } from '@app/helpers/authProvider';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ENV } from "@app/constants/env"
import { Signature } from '@progress/kendo-react-inputs';

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

const rawBaseQuery = fetchBaseQuery({
  // ServiceManagement API uses https://localhost:7043/ without /api/
  // This is different from ENV.JOB_APIURL which includes /api/
  baseUrl: ENV.BASE_API_URL +ENV.JOB_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken(); // ensure this returns a non-empty string
    //const token = sessionStorage.getItem('accessToken'); // Replace 'authToken' with the actual key used in sessionStorage
   
    if (token) {
      let headers: Headers | undefined;
      if ((args as FetchArgs).headers instanceof Headers) {
        headers = (args as FetchArgs).headers as Headers;
      } else if ((args as FetchArgs).headers) {
        headers = new Headers((args as FetchArgs).headers as HeadersInit);
      } else {
        headers = new Headers();
      }

      if (headers) {
        headers.set('Authorization', `Bearer ${token}`); // use standard casing
        (args as FetchArgs).headers = headers;
      }
    }

    return await rawBaseQuery(args, api, extraOptions);
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};
// Transport Codes by Mode API (now after authBaseQuery is defined)
export const transportCodeApi = createApi({
  reducerPath: 'transportCodeApi',
  baseQuery: authBaseQuery,
  endpoints: (builder) => ({
    getTransportCodesByModeId: builder.query<any, { modeId: number, token: string }>(
      {
        query: ({ modeId, token }) => ({
          url: `/CommonLookup/GetTransportCodesByMode?modeOfTransportId=${modeId}`,
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`
          }
        })
      }
    )
  })
});

export const { useGetTransportCodesByModeIdQuery } = transportCodeApi;
  
export const jobServiceApi = createApi({
  reducerPath: 'jobServiceApi',
  baseQuery: authBaseQuery,
  tagTypes: ['JobService'],
  endpoints: (builder) => ({
    // Work Order Service Audit History
    sendSignatureToApi: builder.mutation<any, SignatureDto>({
      query: (signatureData) => {
        // Backend expects only userId in query string
        //const userId = loggedInUserId;//need to implement
        return {
          url: `WorkOrderService/SaveSign?userId=${signatureData.userId}`,
          method: 'POST',
          body: signatureData,
        };
      },
      invalidatesTags: ['JobService'],
    }),
     getImageData: builder.query<any, { mstrWoServiceId: number; userId: number; assignedTo?: number }>({
      query: ({ mstrWoServiceId, userId, assignedTo = 0 }) => ({
        url: `WorkOrderService/GetImageData?mstrWoServiceId=${mstrWoServiceId}&userId=${userId}&assignedTo=${assignedTo}`,
        method: 'POST',
        headers: { accept: '*/*' },
        body: {}, // POST with empty body
      }),
      transformResponse: (response: any) => {
        // Clean any Base64 strings in the response to prevent invalid character errors
        if (response && typeof response === 'object') {
          const cleanedResponse = { ...response };
          
          // List of potential Base64 fields that might be in the response
          const base64Fields = ['SignatureData', 'SignatureData1', 'SignaturePath', 'CustSignature', 'Signature', 'imageData'];
          
          base64Fields.forEach(field => {
            if (cleanedResponse[field] && typeof cleanedResponse[field] === 'string') {
              // Remove any whitespace, newlines, or invalid Base64 characters
              cleanedResponse[field] = cleanedResponse[field].replace(/[^A-Za-z0-9+/=]/g, '');
            }
          });
          
          return cleanedResponse;
        }
        return response;
      },
      }),
    // Get DMS documents by service ID - returns databasefile URLs
    getDocumentsByServiceId: builder.query<any, { mstrWoServiceId: number }>({
      query: ({ mstrWoServiceId }) => ({
        url: `WorkOrderService/GetDocumentsByServiceId?mstrWoServiceId=${mstrWoServiceId}`,
        method: 'GET',
        headers: { accept: '*/*' },
      }),
      transformResponse: (response: any) => {
        console.log('📦 GetDocumentsByServiceId raw response:', response);
        
        // Handle different response formats
        if (Array.isArray(response)) {
          console.log('✅ Response is array, length:', response.length);
          return response;
        }
        
        if (response && response.data && Array.isArray(response.data)) {
          console.log('✅ Response has data array, length:', response.data.length);
          return response.data;
        }
        
        if (response && typeof response === 'object') {
          console.log('⚠️ Response is object but not array format:', Object.keys(response));
          return response;
        }
        
        console.warn('❌ Unexpected response format for GetDocumentsByServiceId:', response);
        return [];
      },
      providesTags: ['JobService'],
    }),
    // Get document upload by ID (from DocumentUpload controller)
    getDocumentUploadById: builder.query<any, string>({
      query: (id) => ({
        url: `${ENV.BASE_API_URL +ENV.DMS_APIURL}/DocumentsUpload/GetDocumentUploadById/${id}`,
        method: 'GET',
        headers: { accept: '*/*' },
      }),
      providesTags: ['JobService'],
    }),
      // Get document uploads by comma-separated IDs (from DocumentUpload controller)
    getAllDocumentUploadById: builder.query<any, string>({
      query: (ids) => ({
        url: `${ENV.BASE_API_URL+ENV.DMS_APIURL}/DocumentsUpload/GetDocumentUploadByIds/${encodeURIComponent(ids)}`,
        method: 'GET',
        headers: { accept: '*/*' },
      }),
      providesTags: ['JobService'],
    }),
    getWoServiceAuditHistory: builder.query<WoServiceAudit[], { workOrderId: number; serviceId: number }>({
      query: ({ workOrderId, serviceId }) => `WorkOrderService/ReadWoServiceAudit/${workOrderId}/${serviceId}`,
    }),
    getAllWOServices: builder.query<JobServiceEntity[], GetAllWOServicesParams>({
      query: (params) => {
        const queryString = `WorkOrderService/GetAllWOServices?UserID=${params.UserID}&Mstr_Show_ID=${params.Mstr_Show_ID}&PageNumber=${params.PageNumber}&PageSize=${params.PageSize}&TotalRecord=${params.TotalRecord}&Row_Number=${params.Row_Number}&Row_Start=${params.row_start}&Row_End=${params.row_end}&filtered=${encodeURIComponent(params.filtered || '')}&SortColumn=${encodeURIComponent(params.SortColumn || 'mstrwoserviceid')}&SortOrder=${encodeURIComponent(params.SortOrder || 'asc')}&WOType=${encodeURIComponent(params.WOType || '')}&sendMail=${params.sendMail || false}`;
       
        return {
          url: queryString,
          method: 'GET',
          headers: {
            'accept': '*/*'
          }
        };
      },
      transformResponse: (response: any) => {
        // If response is an array, return it
        if (Array.isArray(response)) {
          return response;
        }
        
        // If response has a data property that's an array, return it
        if (response && response.data && Array.isArray(response.data)) {
          return response.data;
        }
        
        // Otherwise return empty array
        console.warn('Unexpected response format for getAllWOServices, returning empty array');
        return [];
      },
      providesTags: ['JobService']
    }),
    
    getJobServiceById: builder.query<JobServiceEntity, { mstrWoServiceId: number; userId?: number; assignedTo?: number }>({
      query: ({ mstrWoServiceId, userId, assignedTo }) => {
              const serviceId = Number(mstrWoServiceId);
        const userIdValue = (userId !== undefined && userId !== null) ? Number(userId) : 0;
        const assignedToValue = (assignedTo !== undefined && assignedTo !== null) ? Number(assignedTo) : 0;
        
        // Strict validation
        if (!Number.isInteger(serviceId) || serviceId <= 0) {
          throw new Error(`Invalid service ID: ${mstrWoServiceId}`);
        }
        
        if (!Number.isInteger(userIdValue) || userIdValue < 0) {
          throw new Error(`Invalid userId: ${userId}`);
        }
        
        if (!Number.isInteger(assignedToValue) || assignedToValue < 0) {
          throw new Error(`Invalid assignedTo: ${assignedTo}`);
        }

        // Use URLSearchParams to ensure proper encoding
        const params = new URLSearchParams();
        params.append('mstrWoServiceId', serviceId.toString());
        params.append('userId', userIdValue.toString());
        params.append('assignedTo', assignedToValue.toString());
        
        const url = `WorkOrderService/GetJobServiceById?${params.toString()}`;       
            
        return {
          url: url,
          method: 'GET',
        };
      },
      transformResponse: (response: any) => {
        // Handle different response formats
        if (Array.isArray(response)) {
          return response.length > 0 ? response[0] : {} as JobServiceEntity;
        }
        
        if (response && typeof response === 'object') {
          // If response has a data property, use it; otherwise use response directly
          return (response.data && typeof response.data === 'object' ? response.data : response) as JobServiceEntity;
        }
        
        return {} as JobServiceEntity;
      },
      providesTags: ['JobService']
    }),

    createJobService: builder.mutation<JobServiceEntity, Partial<JobServiceEntity>>({
      query: (jobService) => ({
        url: 'WorkOrderService/CreateJobService',
        method: 'POST',
        body: jobService,
      }),
      invalidatesTags: ['JobService']
    }),

    updateJobService: builder.mutation<void, { serviceData: any; userid?: string | number }>({
      query: ({ serviceData, userid }) => {
        // Add userid to the serviceData since backend expects it in the body
        const requestBody = {
          ...serviceData,
          userid: userid || "0"
        };
        
        return {
          url: 'WorkOrderService/UpdateWoService',
          method: 'POST',
          body: requestBody,
        };
      },
      invalidatesTags: ['JobService']
    }),

    deleteJobService: builder.mutation<any, { serviceid: number; userid: number }>({
      query: ({ serviceid, userid }) => ({
        url: `WorkOrderService/DeleteJobService?serviceid=${serviceid}&userid=${userid}`,
        method: 'POST',
        headers: { 'accept': '*/*' },
        body: {},
      }),
      invalidatesTags: ['JobService']
    }),

    // Get Transport Codes by Mode ID (IATA, UNLOCODE, Railway codes)
    getTransportCodesByMode: builder.query<any[], number>({
      query: (modeId) => ({
        url: `${ENV.BASE_API_URL +ENV.MASTERS_APIURL}/CommonLookup/GetTransportCodesByMode?modeOfTransportId=${modeId}`,
        method: 'GET',
        headers: { 'accept': '*/*' }
      }),
      transformResponse: (response: any) => {
        return Array.isArray(response) ? response : [];
      }
    })
  }),
});

// Helper to fetch transport codes by modeId (with token)
export async function fetchTransportCodesByModeId(modeId: number) {
  // Use authBaseQuery for consistent authentication and error handling
  const result = await authBaseQuery({
    url: `${ENV.BASE_API_URL +ENV.MASTERS_APIURL}/CommonLookup/GetTransportCodesByMode?modeOfTransportId=${modeId}`,
    method: 'GET',
    headers: { 'accept': '*/*' }
  }, { type: 'query' } as any, {});
  if ('error' in result && result.error) {
    throw new Error(result.error && result.error.data ? String(result.error.data) : 'Failed to fetch transport codes');
  }
  // RTK Query returns data in 'data' property
  return (result as any).data;
}
export const {
  useGetAllWOServicesQuery,
  useGetJobServiceByIdQuery,
  useCreateJobServiceMutation,
  useUpdateJobServiceMutation,
  useDeleteJobServiceMutation,
  useGetWoServiceAuditHistoryQuery,
  useSendSignatureToApiMutation,
  useGetImageDataQuery,
  useGetDocumentsByServiceIdQuery,
  useGetDocumentUploadByIdQuery,
  useLazyGetDocumentUploadByIdQuery,
  useGetAllDocumentUploadByIdQuery,
  useLazyGetAllDocumentUploadByIdQuery,
  useGetTransportCodesByModeQuery
} = jobServiceApi;